import { useState, useCallback } from 'react';

const usePayees = () => {
  const [payees, setPayees] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 0, currentPage: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPayees = useCallback(async (projectId, params = {}) => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams(params).toString();
      const res = await fetch(`/api/projects/${projectId}/payees?${query}`, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || 'Failed to fetch payees');
      
      setPayees(data.payees);
      setPagination({
        total: data.total,
        pages: data.pages,
        currentPage: data.currentPage
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const addPayee = async (projectId, name, type = 'Vendor', isActive = true, metadata = {}) => {
    try {
      const res = await fetch(`/api/projects/${projectId}/payees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': localStorage.getItem('token')
        },
        body: JSON.stringify({ name, type, isActive, ...metadata })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.errors?.[0]?.msg || data.msg || 'Failed to add payee');
      setPayees(prev => [data, ...prev].sort((a, b) => a.name.localeCompare(b.name)));
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const updatePayee = async (projectId, payeeId, name, type, isActive, metadata = {}) => {
    try {
      const res = await fetch(`/api/projects/${projectId}/payees/${payeeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': localStorage.getItem('token')
        },
        body: JSON.stringify({ name, type, isActive, ...metadata })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.errors?.[0]?.msg || data.msg || 'Failed to update payee');
      setPayees(prev => prev.map(p => (p._id === payeeId ? data : p)));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  return { payees, pagination, loading, error, fetchPayees, addPayee, updatePayee };
};

export default usePayees;
